import { chain, isFinite, get, find } from "lodash"
import { createSelector } from "reselect"
import { safeDivide } from "helpers/math"
import { compareId } from "helpers/securities"

const EMPTY_OBJECT = {}
export const getForecast = state => get(state, "global.data.forecastData.data.model.simulation.forecast.forecastData", EMPTY_OBJECT)
export const getPortfolioRisk = state => get(state, "global.data.portfolioData.data.model.simulation.risk[0]", EMPTY_OBJECT)
export const getPortfolioData = state => get(state, "global.data.portfolioData.data.model.simulation")
export const getPortfolioPositions = state => get(state, "global.data.portfolioData.data.portfolio.dates[0]")
export const getModuleRiskTarget = state => get(state, "modules.optimization.riskTarget")
export const getCoverage = state => get(state, "global.data.portfolioData.data.model.portfolio.coverage.0", EMPTY_OBJECT)
const getCoveragePercentAvailableGMV = state => get(state, "global.data.portfolioData.data.model.portfolio.coverage.0.summary.percentGmvAvailable", 0)

export const getRawPortfolioEquity = createSelector(
  [getPortfolioPositions],
  (positions) => get(positions, "equity"),
)

export const getPortfolioEquity = createSelector(
  [getPortfolioPositions, getCoveragePercentAvailableGMV],
  (positions, percentGmvAvailable) => {
    const portfolioEquity = get(positions, "equity")
    return isFinite(portfolioEquity) ? portfolioEquity : get(positions, "gmv") * percentGmvAvailable
  },
)

const COMPOSITION_PATH = "global.data.portfolioData.data.model.simulation.composition.0"
const getCompositionData = state => get(state, COMPOSITION_PATH)

export const getCompositionReferenceEquity = createSelector(
  [getCompositionData, getPortfolioEquity],
  (composition, portfolioEquity) => {
    const ref = get(composition, "referenceEquity")
    if (isFinite(ref)) return ref
    const gmv = get(composition, "gmv")
    return isFinite(gmv) ? gmv : portfolioEquity
  },
)

const getRawExpectedReturn = createSelector(
  [getForecast, getPortfolioPositions, getPortfolioEquity],
  (forecast, positions, portfolioEquity) => {
    const expectedEquitiesReturns = get(forecast, "equities")
    const equities = chain(positions)
      .get("equities")
      .sumBy(equity => {
        const size = safeDivide(equity.economicExposure, portfolioEquity)
        const equityReturn = find(expectedEquitiesReturns, erEquity => compareId(equity.id, erEquity.id))

        return equityReturn ? size * equityReturn.expectedPercentReturn : 0
      })
      .value()

    const expectedSwapsReturns = get(forecast, "swaps")
    const swaps = chain(positions)
      .get("swaps")
      .sumBy(swap => {
        const size = safeDivide(swap.economicExposure, portfolioEquity)
        const swapReturn = find(expectedSwapsReturns, erSwap => erSwap.id === swap.id)

        if (swapReturn) {
          return size * swapReturn.expectedPercentReturn
        } else {
          return 0
        }
      })
      .value()

    return equities + swaps
  },
)

// Whatever the difference is between what's computed by the API (which is active)
// and what's computed locally (which is not) is the return of the benchmark
export const getBenchmarkReturn = createSelector(
  [getRawExpectedReturn, getForecast],
  (returnPerc, forecast) => {
    return returnPerc - get(forecast, "total", returnPerc)
  },
)

export const getOriginalForecast = createSelector(
  [getPortfolioRisk, getForecast],
  (summary, impliedReturns) => {
    const total = get(summary, "total", 0)

    return ({
      returnPerc: impliedReturns.total,
      total,
      sharpe: safeDivide(impliedReturns.total, total * 100),
    })
  },
)

export const getRiskTarget = createSelector(
  [getModuleRiskTarget, getOriginalForecast],
  (riskTarget, originalForecast) => {
    if (riskTarget && riskTarget.predictedTotalRisk) {
      return riskTarget
    } else {
      return { predictedTotalRisk: originalForecast.total * 100 }
    }
  },
)
